/**
 * gradeArtifact Stage 0 — pure, synchronous, ZERO model calls, zero I/O.
 *
 * This replaces the vibes judge in `src/lib/ai-agent/helpers/evaluate-content.ts`,
 * which sends {caption, platform, contentType, brandTone} to a PRO model under
 * the persona "expert social media content evaluator who has managed accounts
 * with 100K+ followers". That judge cannot see the image, the brand kit, or any
 * performance data — it scores prose it cannot verify. Every rule here is
 * arithmetic over data already in memory, and every one of them is a constraint
 * a real publisher API actually enforces (or, for the two judgement calls, is
 * capped at WARN and labelled as an estimate).
 *
 * COMPOSITION, NOT REIMPLEMENTATION. `resolvePlacement`,
 * `placementDisabledReason`, `inferPostingMediaType`, `isThreadsSupportedImageUrl`,
 * `isTikTokSupportedPhotoUrl` and `getPlatformSpec` are the SAME functions the
 * publisher path consumes. A second source of truth for publish constraints
 * that drifts from the one `post-dispatcher` enforces would be a chip claiming
 * "passed" while publish is blocked — this repo's documented #1 bug class,
 * committed inside the function built to prevent it.
 */
import { inferPostingMediaType } from '../../../shared/posting-readiness.ts';
import { type MediaKind, resolvePlacement } from '../platform-matrix/placement-resolution.ts';

import { checkAspect } from './checks/aspect.ts';
import {
  checkCaptionCta,
  checkCaptionScene,
  checkCaptionSlop,
} from './checks/caption-substance.ts';
import { checkCaptionFold, checkCaptionLength } from './checks/caption.ts';
import { checkFormat } from './checks/format.ts';
import { checkImageSubject } from './checks/image-subject.ts';
import { checkLink } from './checks/link.ts';
import { checkMediaKind } from './checks/media-kind.ts';
import { checkPlatformRules } from './checks/platform-rules.ts';
import { CHECK_ORDER, type QualityBag, type QualityCheck, type QualityVerdict } from './types.ts';

export interface GradeArtifactInput {
  platform: string;
  caption: string;
  mediaUrls: string[];
  mediaType: string | null;
  placementOverride?: string | null;
  aspectOverride?: string | null;
  /**
   * W/H of the SOURCE image, or null when unmeasured. Null is honest and
   * produces a `skipped` aspect check — never a `pass`. See checks/aspect.ts
   * for why the server genuinely cannot know this.
   */
  sourceRatio?: number | null;
  /** Post carries a shopify_product_id, source_url or source_article_id. */
  hasCommerceContext?: boolean;
  /**
   * True when the campaign this post belongs to exists to SELL (goal
   * drive_sales / product_launch / sale / launch / product_showcase, or a
   * free-text goal about selling). Undefined is honest and produces a
   * `skipped` caption_cta check — never a `pass`. Same discipline as
   * `sourceRatio`: a guess reported as a result is the bug, not the gap.
   */
  salesGoal?: boolean;
  /**
   * The subject the image was COMMISSIONED to show — the scene text actually
   * sent to the image model. Undefined is honest and produces a `skipped`
   * image_subject check, never a `pass`: every post generated before the
   * caption-first derivation shipped has no such record, and every uploaded
   * image never had one. Same discipline as `sourceRatio` and `salesGoal`.
   */
  imageSubject?: string | null;
  /** `caption` (derived from the finished caption) or `topic` (fallback). */
  imageSubjectSource?: 'caption' | 'topic' | null;
}

export function gradeArtifact(input: GradeArtifactInput, now: Date = new Date()): QualityVerdict {
  const mediaUrls = Array.isArray(input.mediaUrls)
    ? input.mediaUrls.filter(
        (url): url is string => typeof url === 'string' && url.trim().length > 0,
      )
    : [];

  // Derive the KIND from the live url list rather than trusting the stored
  // label: posting-readiness.ts:428-437 documents why (schedule-post patches a
  // client-supplied media_type verbatim, so a stale 'image' label can carry a
  // video url).
  const mediaKind = inferPostingMediaType(
    input.mediaType as MediaKind | null,
    mediaUrls,
  ) as MediaKind;

  // The SAME resolver the preview, the picker, the dispatcher and readiness
  // all use — so the grade can never claim a placement the publisher won't use.
  const row = resolvePlacement({
    platform: input.platform,
    mediaKind,
    override: input.placementOverride ?? null,
  });

  const caption = input.caption ?? '';
  const aspectOverride = input.aspectOverride ?? null;
  const hasAspectOverride = Boolean(aspectOverride);

  const byId: Record<string, QualityCheck> = {
    format: checkFormat({
      platform: input.platform,
      mediaUrls,
      mediaKind,
      hasAspectOverride,
      row,
    }),
    aspect: checkAspect({
      platform: input.platform,
      row,
      sourceRatio: input.sourceRatio ?? null,
      aspectOverride,
      mediaKind,
    }),
    media_kind: checkMediaKind({ platform: input.platform, row, mediaKind }),
    caption_length: checkCaptionLength({ platform: input.platform, caption }),
    caption_fold: checkCaptionFold({ platform: input.platform, caption }),
    link: checkLink({
      platform: input.platform,
      caption,
      hasCommerceContext: Boolean(input.hasCommerceContext),
    }),
    caption_cta: checkCaptionCta({ caption, salesGoal: input.salesGoal }),
    caption_scene: checkCaptionScene({ caption, mediaUrls }),
    caption_slop: checkCaptionSlop({ caption }),
    image_subject: checkImageSubject({
      caption,
      mediaUrls,
      imageSubject: input.imageSubject ?? null,
      imageSubjectSource: input.imageSubjectSource ?? null,
    }),
    platform_rules: checkPlatformRules(caption),
  };

  const checks = CHECK_ORDER.map((id) => byId[id]);

  const failCount = checks.filter((check) => check.status === 'fail').length;
  const warnCount = checks.filter((check) => check.status === 'warn').length;
  const passCount = checks.filter((check) => check.status === 'pass').length;
  const skippedCount = checks.filter((check) => check.status === 'skipped').length;

  return {
    version: 1,
    platform: input.platform,
    placement: row?.placement ?? null,
    mediaKind,
    gradedAt: now.toISOString(),
    status: failCount > 0 ? 'fail' : warnCount > 0 ? 'warn' : 'pass',
    checks,
    passCount,
    warnCount,
    failCount,
    skippedCount,
  };
}

export interface GradePostInput extends Omit<GradeArtifactInput, 'platform'> {
  platforms: string[];
}

/** One verdict per targeted platform — the shape persisted to `social_posts.quality`. */
export function gradePost(input: GradePostInput, now: Date = new Date()): QualityBag {
  const platforms = Array.isArray(input.platforms)
    ? input.platforms.filter((p): p is string => typeof p === 'string' && p.trim().length > 0)
    : [];

  const byPlatform: Record<string, QualityVerdict> = {};
  for (const platform of platforms) {
    byPlatform[platform] = gradeArtifact({ ...input, platform }, now);
  }

  const statuses = Object.values(byPlatform).map((verdict) => verdict.status);
  const worst = statuses.includes('fail') ? 'fail' : statuses.includes('warn') ? 'warn' : 'pass';

  return { version: 1, gradedAt: now.toISOString(), worst, byPlatform };
}

/**
 * Deep-equal on everything EXCEPT `gradedAt`. The client uses this to avoid a
 * pointless write-back on every inspector open — and, more importantly, to
 * stop the write-back effect from firing on every keystroke of the caption
 * editor, which would be one Supabase UPDATE per character.
 */
export function verdictsEqual(
  a: QualityVerdict | null | undefined,
  b: QualityVerdict | null | undefined,
): boolean {
  if (!a || !b) return a === b;
  if (
    a.platform !== b.platform ||
    a.placement !== b.placement ||
    a.mediaKind !== b.mediaKind ||
    a.status !== b.status ||
    a.checks.length !== b.checks.length
  ) {
    return false;
  }
  return a.checks.every((check, i) => {
    const other = b.checks[i];
    return (
      check.id === other.id &&
      check.status === other.status &&
      check.detail === other.detail &&
      (check.fix?.id ?? null) === (other.fix?.id ?? null) &&
      (check.fix?.captionReplacement ?? null) === (other.fix?.captionReplacement ?? null) &&
      (check.fix?.perPlatformPatch?.set?.aspectRatio ?? null) ===
        (other.fix?.perPlatformPatch?.set?.aspectRatio ?? null)
    );
  });
}
